import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, BookOpen, Coins, TrendingUp, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { getOfferings } from "@/services/OfferingServices";
import { fetchTeacherCoinSummary, fetchTeacherCoinSpends } from "@/services/teacherProfileService";
import { startOfMonth } from "date-fns";

interface GlanceStripProps {
  accessToken: string;
  unreadCount: number;
  coinBalance: number | null;
}

function yyyyMM(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function GlanceStrip({ accessToken, unreadCount, coinBalance }: GlanceStripProps) {
  const { data: offeringsData } = useQuery({
    queryKey: ["teacher-offerings-glance"],
    queryFn: () => getOfferings(accessToken),
    staleTime: 2 * 60 * 1000,
    enabled: !!accessToken,
  });

  const { data: coinSummary } = useQuery({
    queryKey: ["teacherCoinSummary"],
    queryFn: () => fetchTeacherCoinSummary(accessToken),
    staleTime: 5 * 60 * 1000,
    enabled: !!accessToken,
  });

  const thisMonthStart = startOfMonth(new Date());
  const { data: monthSpends } = useQuery({
    queryKey: ["teacherCoinSpends-glance", yyyyMM(new Date())],
    queryFn: () => fetchTeacherCoinSpends(accessToken, { page: 0, size: 100, dateFrom: thisMonthStart }),
    staleTime: 2 * 60 * 1000,
    enabled: !!accessToken,
  });

  const rawOfferings = Array.isArray(offeringsData)
    ? offeringsData
    : (offeringsData as any)?.offerings ?? [];
  const activeOfferings = rawOfferings.filter(
    (o: any) => o.status === "PUBLISHED" || o.active === true,
  ).length;

  const chargeAmount = coinSummary?.chargeAmount ?? 30;
  const repliesRemaining = coinBalance != null ? Math.floor(coinBalance / chargeAmount) : null;
  const repliesThisMonth = (monthSpends?.content ?? []).filter((r: any) => r.amount < 0).length;

  const lowCoins = coinBalance != null && coinBalance < chargeAmount;

  const tiles = [
    {
      to: "/teacher-inquiries",
      Icon: MessageSquare,
      label: "Unread Inquiries",
      valueStr: unreadCount === 0 ? "0" : String(unreadCount),
      hint: unreadCount > 0 ? "Tap to view & reply" : "All caught up",
      urgent: unreadCount > 0,
      iconBg: unreadCount > 0 ? "bg-rose-100" : "bg-slate-100",
      iconColor: unreadCount > 0 ? "text-rose-500" : "text-slate-400",
      valueCls: unreadCount > 0 ? "text-rose-600" : "text-slate-400",
      pulse: unreadCount > 0,
    },
    {
      to: "/teacher-offerings",
      Icon: BookOpen,
      label: "Active Offerings",
      valueStr: String(activeOfferings),
      hint: activeOfferings > 0 ? "Live in search results" : "None published yet",
      urgent: false,
      iconBg: activeOfferings > 0 ? "bg-violet-100" : "bg-slate-100",
      iconColor: activeOfferings > 0 ? "text-violet-500" : "text-slate-400",
      valueCls: activeOfferings > 0 ? "text-violet-700" : "text-slate-400",
      pulse: false,
    },
    {
      to: "/teacher-coins",
      Icon: Coins,
      label: "BMC Coins Left",
      valueStr: coinBalance != null ? coinBalance.toLocaleString() : "—",
      hint: repliesRemaining != null
        ? `${repliesRemaining} ${repliesRemaining === 1 ? "reply" : "replies"} remaining`
        : "Top up to reply",
      urgent: lowCoins,
      iconBg: lowCoins ? "bg-amber-100" : "bg-indigo-100",
      iconColor: lowCoins ? "text-amber-500" : "text-indigo-500",
      valueCls: lowCoins ? "text-amber-600" : "text-indigo-700",
      pulse: false,
    },
    {
      to: "/teacher-coins",
      Icon: repliesThisMonth > 0 ? TrendingUp : Zap,
      label: "Replies This Month",
      valueStr: String(repliesThisMonth),
      hint: repliesThisMonth > 0 ? "Student inquiries replied" : "No replies yet this month",
      urgent: false,
      iconBg: repliesThisMonth > 0 ? "bg-emerald-100" : "bg-slate-100",
      iconColor: repliesThisMonth > 0 ? "text-emerald-500" : "text-slate-400",
      valueCls: repliesThisMonth > 0 ? "text-emerald-700" : "text-slate-400",
      pulse: false,
    },
  ];

  return (
    <div className="flex items-stretch border-b border-slate-100 bg-white shrink-0 overflow-x-auto">
      {tiles.map((tile, i) => (
        <Link
          key={tile.label}
          to={tile.to}
          className={cn(
            "flex items-center gap-3 px-5 py-3.5 min-w-[200px] flex-1 hover:bg-slate-50 transition-colors group",
            i > 0 && "border-l border-slate-100",
          )}
        >
          {/* Icon */}
          <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center shrink-0", tile.iconBg)}>
            <tile.Icon className={cn("w-4 h-4", tile.iconColor)} />
          </div>

          {/* Text */}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide leading-none mb-1">
              {tile.label}
            </p>
            <div className="flex items-center gap-1.5">
              <p className={cn("text-xl font-black leading-none", tile.valueCls)}>
                {tile.valueStr}
              </p>
              {tile.pulse && (
                <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0" />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5 truncate group-hover:text-slate-600 transition-colors">
              {tile.hint}
            </p>
          </div>
        </Link>
      ))}
    </div>
  );
}
